import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { toast } from "react-toastify";
import { exportIPDPatientSummaryApi, getIPDPatientSummaryApi } from "../services/reports.service";
import { reportDayEndISO, reportDayStartISO } from "../utils/reportDateRange";

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)", // Background color
  width: "44px", // Width
  height: "44px", // Height
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const headLabel = [
  {
    id: 1,
    label: "SNO",
    value: "sno",
  },
  {
    id: 2,
    label: "Patient Id ",
    value: "patientId",
  },
  {
    id: 3,
    label: "Patient Name",
    value: "patientname",
  },
  {
    id: 4,
    label: "Age",
    value: "age",
  },
  {
    id: 5,
    label: "Sex",
    value: "sex",
  },
  {
    id: 6,
    label: "Ward",
    value: "ward",
  },
];

// Proper dropdown options
const templateOptions = [
  { label: "All IPD Patient Summary", value: "all_ipd_summary" },
  { label: "Active Patients", value: "active_patients" },
  { label: "Discharged Patients", value: "discharged_patients" },
  { label: "Critical Patients", value: "critical_patients" },
];

const uhidOptions = [
  { label: "PID1001", value: "PID1001" },
  { label: "PID1002", value: "PID1002" },
  { label: "PID1003", value: "PID1003" },
  { label: "PID1004", value: "PID1004" },
  { label: "PID1005", value: "PID1005" },
];

const wardOptions = [
  { label: "General Ward", value: "general" },
  { label: "ICU", value: "icu" },
  { label: "CCU", value: "ccu" },
  { label: "Emergency", value: "emergency" },
  { label: "Surgery", value: "surgery" },
  { label: "Pediatrics", value: "pediatrics" },
];

const blockOptions = [
  { label: "Block A", value: "block_a" },
  { label: "Block B", value: "block_b" },
  { label: "Block C", value: "block_c" },
  { label: "Block D", value: "block_d" },
];

const unitOptions = [
  { label: "Unit 1", value: "unit_1" },
  { label: "Unit 2", value: "unit_2" },
  { label: "Unit 3", value: "unit_3" },
  { label: "Unit 4", value: "unit_4" },
];

const appropriateOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
  { label: "Pending", value: "pending" },
];

const emcsOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const regStatusOptions = [
  { label: "Registered", value: "registered" },
  { label: "Pending", value: "pending" },
  { label: "Cancelled", value: "cancelled" },
];

const visitNoOptions = [
  { label: "V001", value: "v001" },
  { label: "V002", value: "v002" },
  { label: "V003", value: "v003" },
  { label: "V004", value: "v004" },
];

export default function ReportsIPDPatientSummary() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    searchTemplate: null,
    uhid: null,
    ward: null,
    block: null,
    unitName: null,
    appropriate: null,
    isEmcs: null,
    ipRegStatus: null,
    appropriate2: null,
    admissionDate: null,
    visitNo: null,
    startDate: null,
    endDate: null,
  });

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors

  // Download menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    sno: true,
    patientId: true,
    patientname: true,
    age: true,
    sex: true,
    ward: true,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle filter icon click (column visibility)
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'IPD Patient Summary',
        text: 'IPD Patient Summary Report',
        url: window.location.href,
      }).catch((error) => {
        console.log('Error sharing:', error);
      });
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Report URL copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy URL');
      });
    }
  };

  // Handle menu item clicks
  const handleMenuClick = (item) => {
    handleClose();
    switch (item) {
      case 'Excel':
        handleExport();
        break;
      case 'PDF':
        toast.info('PDF export feature coming soon');
        break;
      case 'Report Mail Send Scheduler':
        toast.info('Report Mail Send Scheduler feature coming soon');
        break;
      case 'Report Scheduler':
        toast.info('Report Scheduler feature coming soon');
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.admissionDate) {
      errorFields.push('admissionDate');
    }
    if (!formData.startDate) {
      errorFields.push('startDate');
    }
    if (!formData.endDate) {
      errorFields.push('endDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    setIsLoading(true);
    
    try {
      const params = {
        searchTemplate: formData.searchTemplate?.value,
        admissionDate: reportDayStartISO(formData.admissionDate),
        startDate: reportDayStartISO(formData.startDate),
        endDate: reportDayEndISO(formData.endDate),
        page: 1,
        limit: 200,
      };
      Object.keys(params).forEach((k) => {
        if (params[k] === undefined || params[k] === "") delete params[k];
      });

      const res = await getIPDPatientSummaryApi(params);
      if (res?.success === false || res?.isSuccess === false) {
        toast.error(res?.message || "Failed to load IPD summary");
        setTableData([]);
        return;
      }
      const rows = res?.data?.records || [];
      setTableData(rows);
      toast.success(rows.length ? "Report data loaded successfully" : "No IPD patients matched.");
    } catch (error) {
      console.error("Error fetching report data:", error);
      toast.error("Failed to load report data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (tableData.length === 0) {
      toast.warning("Please search for data before exporting");
      return;
    }
    
    setIsExporting(true);
    
    try {
      // Prepare export parameters
      const exportParams = {
        admissionDate: reportDayStartISO(formData.admissionDate),
        startDate: reportDayStartISO(formData.startDate),
        endDate: reportDayEndISO(formData.endDate),
        page: 1,
        limit: 200,
      };

      // Remove undefined/empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined || exportParams[key] === '' || exportParams[key] === null) {
          delete exportParams[key];
        }
      });

      const result = await exportIPDPatientSummaryApi(exportParams);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              IPD Patient Summary
            </Typography>
          </Box>
          <Box>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => handleInputChange('searchTemplate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={uhidOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.uhid}
                  onChange={(event, newValue) => handleInputChange('uhid', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="UHID" name="uhid" fullWidth />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={wardOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.ward}
                  onChange={(event, newValue) => handleInputChange('ward', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Ward" name="ward" fullWidth />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={blockOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.block}
                  onChange={(event, newValue) => handleInputChange('block', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Block"
                      name="block"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={unitOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.unitName}
                  onChange={(event, newValue) => handleInputChange('unitName', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Unit Name"
                      name="unitName"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={appropriateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.appropriate}
                  onChange={(event, newValue) => handleInputChange('appropriate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Appropriate"
                      name="appropriate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={emcsOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.isEmcs}
                  onChange={(event, newValue) => handleInputChange('isEmcs', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Is EMCS"
                      name="isEmcs"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={regStatusOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.ipRegStatus}
                  onChange={(event, newValue) => handleInputChange('ipRegStatus', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ip-Reg Status"
                      name="ipRegStatus"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={appropriateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.appropriate2}
                  onChange={(event, newValue) => handleInputChange('appropriate2', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Appropriate"
                      name="appropriate2"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  name="admissionDate"
                  label="Admission Date"
                  value={formData.admissionDate}
                  onChange={(newValue) => {
                    handleInputChange('admissionDate', newValue);
                    // Clear error when field is changed
                    if (errors.includes('admissionDate')) {
                      setErrors(errors.filter(e => e !== 'admissionDate'));
                    }
                  }}
                  slotProps={{
                    textField: { 
                      required: true, 
                      fullWidth: true,
                      InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                    },
                  }}
                  sx={{ 
                    bgcolor: "white",
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: errors?.includes('admissionDate') ? '1px solid red' : '1px solid inherit',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={visitNoOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.visitNo}
                  onChange={(event, newValue) => handleInputChange('visitNo', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Visit No"
                      name="visitNo"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="startDate"
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(newValue) => {
                        handleInputChange('startDate', newValue);
                        // Clear error when field is changed
                        if (errors.includes('startDate')) {
                          setErrors(errors.filter(e => e !== 'startDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('startDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="endDate"
                      label="End Date"
                      value={formData.endDate}
                      onChange={(newValue) => {
                        handleInputChange('endDate', newValue);
                        // Clear error when field is changed
                        if (errors.includes('endDate')) {
                          setErrors(errors.filter(e => e !== 'endDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('endDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting || tableData.length === 0}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              my: "20px",
              p: "20px",
            }}
          >
              {/* dynamic button */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    pb: "15px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                  >
                    Rows: {tableData?.length || 0} | Columns: {Object.values(visibleColumns).filter(v => v).length}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={iconStyleProps} onClick={handleFilterClick}>
                      <EditIcon
                        sx={{
                          cursor: "pointer",
                          color: "transparent",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleShare}>
                      <ShareIcon 
                        sx={{
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps}>
                      <IconButton onClick={handleClick}>
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <TableContainer component={Paper}>
                <Table aria-label="ipd patient summary table" sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": { fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      {headLabel?.map((option) => (
                        visibleColumns[option?.value] && (
                          <TableCell
                            key={option?.id}
                            sx={{ textAlign: option?.align || "left" }}
                          >
                            {option?.label}
                          </TableCell>
                        )
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress />
                          <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : !Array.isArray(tableData) || tableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((row) => (
                        <TableRow key={row?._id}>
                          {headLabel?.map((column, index) => (
                            visibleColumns[column?.value] && (
                              <TableCell key={column?.id}>
                                {row?.[column?.value]}
                              </TableCell>
                            )
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "250px",
                  },
                }}
              >
                {menuItems.map((item, index) => (
                  <React.Fragment key={item}>
                    <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                      {item}
                    </MenuItem>
                    {index !== menuItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Menu>
              
              {/* Filter Menu for Column Visibility */}
              <Menu
                anchorEl={filterAnchorEl}
                open={filterMenuOpen}
                onClose={handleFilterClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "200px",
                  },
                }}
              >
                <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                  Show/Hide Columns
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => toggleColumn('sno')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>SNO</Box>
                    {visibleColumns.sno && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientId')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient Id</Box>
                    {visibleColumns.patientId && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientname')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient Name</Box>
                    {visibleColumns.patientname && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('age')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Age</Box>
                    {visibleColumns.age && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('sex')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Sex</Box>
                    {visibleColumns.sex && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('ward')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Ward</Box>
                    {visibleColumns.ward && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
              </Menu>
            </Box>
        </Box>
      </Box>
    </Container>
  );
}
